import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import GenericModal from "./GenericModal";
import { fetchDailySummaryRange } from "../../services/eventService";
import useUserStore from "../../stores/userStore";
import useModalStore from "../../stores/modalStore";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    Legend, ResponsiveContainer,
} from "recharts";
import { Loader2, AlertCircle, ChevronDown, Gift, GitCompare } from "lucide-react";

const METRIC_CONFIG = {
    hours:      { label: "Hours Worked",  color: "#3b82f6", format: (v) => `${v}h` },
    revenue:    { label: "Revenue",       color: "#22c55e", format: (v) => `$${v.toLocaleString()}` },
    tips:       { label: "Tips",          color: "#a855f7", format: (v) => `$${v.toLocaleString()}` },
    laborCost:    { label: "Base + Merit Cost", color: "#ef4444", format: (v) => `$${v.toLocaleString()}` },
    employerTax:  { label: "Employer Tax",  color: "#dc2626", format: (v) => `$${v.toLocaleString()}` },
    rent:         { label: "Rent",          color: "#b45309", format: (v) => `$${v.toLocaleString()}` },
    pets:         { label: "Pets Serviced", color: "#f59e0b", format: (v) => v },
    groomers:     { label: "Groomers",      color: "#0ea5e9", format: (v) => v },
    revPerHour:   { label: "Rev/Hour",      color: "#64748b", format: (v) => `$${v.toFixed(2)}` },
};

const PRESETS = [
    { label: "7d",   days: 7 },
    { label: "14d",  days: 14 },
    { label: "30d",  days: 30 },
    { label: "60d",  days: 60 },
    { label: "90d",  days: 90 },
    { label: "180d", days: 180 },
    { label: "1yr",  days: 365 },
    { label: "Max",  days: null },
];

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const buildMonthPresets = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed
    const presets = [];
    // Start from Jan 2025 up to the current month
    let y = 2025, m = 0;
    while (y < currentYear || (y === currentYear && m <= currentMonth)) {
        const lastDay = new Date(y, m + 1, 0).getDate();
        presets.push({
            label: `${MONTH_NAMES[m]} ${String(y).slice(2)}`,
            start: `${y}-${String(m + 1).padStart(2, "0")}-01`,
            end: `${y}-${String(m + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`,
        });
        m++;
        if (m > 11) { m = 0; y++; }
    }
    return presets;
};

const MONTH_PRESETS = buildMonthPresets();

const buildYearPresets = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const presets = [];
    for (let y = 2025; y <= currentYear; y++) {
        const endDate = y === currentYear
            ? `${y}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`
            : `${y}-12-31`;
        presets.push({ label: String(y), start: `${y}-01-01`, end: endDate });
    }
    return presets;
};

const YEAR_PRESETS = buildYearPresets();

const CUMULATIVE_KEYS = ["hours", "revenue", "tips", "pets", "laborCost", "employerTax", "rent"];

const toCumulative = (rows, keys) => {
    const acc = {};
    keys.forEach((k) => { acc[k] = 0; });
    return rows.map((row) => {
        const out = { ...row };
        keys.forEach((k) => {
            if (row[k] != null) { acc[k] += row[k]; out[k] = Math.round(acc[k] * 100) / 100; }
        });
        if (out.hours > 0) out.revPerHour = Math.round((out.revenue / out.hours) * 100) / 100;
        else out.revPerHour = 0;
        return out;
    });
};

const formatDate = (d) => {
    const date = new Date(d + "T12:00:00");
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const formatTooltipDate = (d) => {
    const date = new Date(d + "T12:00:00");
    return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};

const LOCATIONS = [
    { key: "all", label: "All Salons" },
    { key: "hopkins", label: "Hopkins" },
    { key: "inver_grove", label: "Inver Grove" },
];

const toISODate = (d) => d.toISOString().split("T")[0];
const firstNameKey = (name) => String(name || "").trim().split(/\s+/)[0].toLowerCase();
const nameKey = (name) => String(name || "").trim().replace(/\s+/g, " ").toLowerCase();
const roundNumber = (value) => Math.round((Number(value) || 0) * 100) / 100;

const mergeEmployeeMetrics = (employeeData, canonicalizeEmployeeName) => {
    const merged = {};
    for (const [rawName, metrics] of Object.entries(employeeData || {})) {
        const canonicalName = canonicalizeEmployeeName(rawName);
        if (!canonicalName) continue;
        if (!merged[canonicalName]) merged[canonicalName] = {};
        for (const [key, value] of Object.entries(metrics || {})) {
            merged[canonicalName][key] = roundNumber((merged[canonicalName][key] || 0) + Number(value || 0));
        }
    }
    return merged;
};

const CustomTooltip = ({ active, payload, label, visibleMetrics, compareMode, primaryLabel, compareLabel }) => {
    if (!active || !payload?.length) return null;
    if (compareMode) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs min-w-[200px]">
                <div className="font-semibold text-gray-700 mb-1.5">Day {label}</div>
                {Object.entries(METRIC_CONFIG)
                    .filter(([key]) => visibleMetrics.includes(key))
                    .map(([key, cfg]) => {
                        const primary = payload.find((p) => p.dataKey === key);
                        const compare = payload.find((p) => p.dataKey === `${key}_cmp`);
                        if (!primary && !compare) return null;
                        return (
                            <div key={key} className="mb-1">
                                <div className="font-medium mb-0.5" style={{ color: cfg.color }}>{cfg.label}</div>
                                <div className="flex justify-between gap-4 pl-2">
                                    <span className="text-gray-500">{primaryLabel}</span>
                                    <span className="font-medium text-gray-800">{primary?.value != null ? cfg.format(primary.value) : "—"}</span>
                                </div>
                                <div className="flex justify-between gap-4 pl-2">
                                    <span className="text-gray-400">{compareLabel}</span>
                                    <span className="font-medium text-gray-500">{compare?.value != null ? cfg.format(compare.value) : "—"}</span>
                                </div>
                            </div>
                        );
                    })}
            </div>
        );
    }
    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
            <div className="font-semibold text-gray-700 mb-1.5">{formatTooltipDate(label)}</div>
            {payload
                .filter((p) => visibleMetrics.includes(p.dataKey))
                .map((p) => (
                    <div key={p.dataKey} className="flex justify-between gap-4">
                        <span style={{ color: p.color }}>{METRIC_CONFIG[p.dataKey]?.label}</span>
                        <span className="font-medium text-gray-800">
                            {METRIC_CONFIG[p.dataKey]?.format(p.value)}
                        </span>
                    </div>
                ))}
        </div>
    );
};

const BusinessTrendsModal = () => {
    const { currentUser, employees: storeEmployees } = useUserStore();
    const showBusinessTrendsModal = useModalStore((s) => s.showBusinessTrendsModal);

    const [data, setData] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [bonuses, setBonuses] = useState([]);
    const [excludedEmployees, setExcludedEmployees] = useState(new Set());
    const [showEmpDropdown, setShowEmpDropdown] = useState(false);
    const empDropdownRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [visibleMetrics, setVisibleMetrics] = useState(["hours", "revenue", "tips", "pets"]);
    const [selectedLocation, setSelectedLocation] = useState("all");

    // Date range state — default last 14 days
    const today = new Date();
    const defaultEnd = toISODate(today);
    const defaultStart = toISODate(new Date(today.getTime() - 13 * 86400000));
    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(defaultEnd);

    const fetchData = useCallback(async () => {
        if (!currentUser?.user_id || !startDate || !endDate) return;
        setLoading(true);
        setError(null);
        try {
            const result = await fetchDailySummaryRange(startDate, endDate, currentUser.user_id);
            setData(result.data || []);
            setEmployees(result.employees || []);
            setBonuses(result.pay_period_bonuses || []);
        } catch (err) {
            setError(err?.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, currentUser?.user_id]);

    useEffect(() => {
        if (showBusinessTrendsModal && currentUser?.user_id) {
            fetchData();
        }
    }, [showBusinessTrendsModal, fetchData, currentUser?.user_id]);

    // Close employee dropdown on outside click
    useEffect(() => {
        if (!showEmpDropdown) return;
        const handler = (e) => {
            if (empDropdownRef.current && !empDropdownRef.current.contains(e.target)) {
                setShowEmpDropdown(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [showEmpDropdown]);

    const [isMax, setIsMax] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);
    const [compareMode, setCompareMode] = useState(false);
    const [compareMonth, setCompareMonth] = useState(null);
    const [compareYear, setCompareYear] = useState(null);
    const [compareData, setCompareData] = useState([]);
    const [compareLoading, setCompareLoading] = useState(false);
    const [cumulative, setCumulative] = useState(false);

    const canonicalByNameKey = useMemo(() => {
        const map = new Map();
        const rawNames = new Set(employees.filter(Boolean));
        const collectPointNames = (point) => {
            for (const rawName of Object.keys(point.by_employee || {})) rawNames.add(rawName);
            for (const rawName of Object.keys(point.hopkins?.by_employee || {})) rawNames.add(rawName);
            for (const rawName of Object.keys(point.inver_grove?.by_employee || {})) rawNames.add(rawName);
        };
        for (const point of data || []) collectPointNames(point);
        for (const point of compareData || []) collectPointNames(point);
        for (const bonus of bonuses || []) {
            for (const rawName of Object.keys(bonus.by_employee || {})) rawNames.add(rawName);
        }

        const rawNameGroups = {};
        for (const rawName of rawNames) {
            const first = firstNameKey(rawName);
            if (!first) continue;
            if (!rawNameGroups[first]) rawNameGroups[first] = [];
            rawNameGroups[first].push(String(rawName).trim().replace(/\s+/g, " "));
        }

        for (const names of Object.values(rawNameGroups)) {
            const uniqueNames = Array.from(new Set(names));
            const byLastToken = {};
            for (const name of uniqueNames) {
                const parts = name.trim().split(/\s+/);
                if (parts.length < 2) continue;
                const last = parts[parts.length - 1].toLowerCase();
                if (!byLastToken[last]) byLastToken[last] = [];
                byLastToken[last].push(name);
            }
            for (const sameLastNames of Object.values(byLastToken)) {
                if (sameLastNames.length < 2) continue;
                const canonical = sameLastNames.sort((a, b) => b.length - a.length)[0];
                for (const name of sameLastNames) {
                    map.set(nameKey(name), canonical);
                }
            }

            const fullName = uniqueNames
                .filter((name) => name.trim().split(/\s+/).length > 1)
                .sort((a, b) => b.length - a.length)[0];
            if (fullName) {
                for (const name of uniqueNames) {
                    map.set(nameKey(name), fullName);
                }
            }
        }

        const firstNameCounts = {};
        for (const employee of storeEmployees || []) {
            const firstName = String(employee.first_name || "").trim();
            if (!firstName) continue;
            const key = nameKey(firstName);
            firstNameCounts[key] = (firstNameCounts[key] || 0) + 1;
        }
        for (const employee of storeEmployees || []) {
            const firstName = String(employee.first_name || "").trim();
            const preferredFirstName = String(employee.preferred_first_name || "").trim();
            const lastName = String(employee.last_name || "").trim();
            const alternateLastName = String(employee.alternate_last_name || "").trim();
            if (!firstName) continue;
            const displayName = `${firstName} ${lastName}`.trim();
            const hasFullName = displayName.trim().split(/\s+/).length > 1;
            if (hasFullName) {
                map.set(nameKey(displayName), displayName);
                if (preferredFirstName) {
                    map.set(nameKey(`${preferredFirstName} ${lastName}`), displayName);
                    map.set(nameKey(preferredFirstName), displayName);
                }
                if (alternateLastName) {
                    map.set(nameKey(`${firstName} ${alternateLastName}`), displayName);
                    if (preferredFirstName) {
                        map.set(nameKey(`${preferredFirstName} ${alternateLastName}`), displayName);
                    }
                }
            }
            if (firstNameCounts[nameKey(firstName)] === 1 && (hasFullName || !map.has(nameKey(firstName)))) {
                map.set(nameKey(firstName), displayName);
            }
        }
        return map;
    }, [bonuses, compareData, data, employees, storeEmployees]);

    const canonicalizeEmployeeName = useCallback((name) => {
        const trimmed = String(name || "").trim().replace(/\s+/g, " ");
        return canonicalByNameKey.get(nameKey(trimmed)) || trimmed;
    }, [canonicalByNameKey]);

    const normalizePoint = useCallback((point) => {
        const normalizeLocation = (location) => location
            ? { ...location, by_employee: mergeEmployeeMetrics(location.by_employee, canonicalizeEmployeeName) }
            : location;
        return {
            ...point,
            by_employee: mergeEmployeeMetrics(point.by_employee, canonicalizeEmployeeName),
            hopkins: normalizeLocation(point.hopkins),
            inver_grove: normalizeLocation(point.inver_grove),
        };
    }, [canonicalizeEmployeeName]);

    const normalizedData = useMemo(() => data.map(normalizePoint), [data, normalizePoint]);
    const normalizedCompareData = useMemo(() => compareData.map(normalizePoint), [compareData, normalizePoint]);
    const normalizedBonuses = useMemo(() => bonuses.map((bonus) => ({
        ...bonus,
        by_employee: Object.entries(bonus.by_employee || {}).reduce((acc, [rawName, amount]) => {
            const canonicalName = canonicalizeEmployeeName(rawName);
            acc[canonicalName] = roundNumber((acc[canonicalName] || 0) + Number(amount || 0));
            return acc;
        }, {}),
    })), [bonuses, canonicalizeEmployeeName]);
    const displayEmployees = useMemo(() => {
        const names = new Set(employees.map(canonicalizeEmployeeName).filter(Boolean));
        for (const point of normalizedData) {
            for (const name of Object.keys(point.by_employee || {})) names.add(name);
        }
        return Array.from(names).sort();
    }, [canonicalizeEmployeeName, employees, normalizedData]);

    useEffect(() => {
        setExcludedEmployees((prev) => new Set([...prev].filter((name) => displayEmployees.includes(name))));
    }, [displayEmployees]);

    const clearCompare = () => { setCompareMonth(null); setCompareYear(null); setCompareData([]); };

    const applyPreset = (days) => {
        const end = new Date();
        if (days === null) {
            setStartDate("2025-01-01");
            setIsMax(true);
        } else {
            const start = new Date(end.getTime() - (days - 1) * 86400000);
            setStartDate(toISODate(start));
            setIsMax(false);
        }
        setEndDate(toISODate(end));
        setSelectedMonth(null);
        setSelectedYear(null);
        clearCompare();
    };

    const applyMonth = (mp) => {
        setStartDate(mp.start);
        setEndDate(mp.end);
        setSelectedMonth(mp.label);
        setSelectedYear(null);
        setIsMax(false);
        clearCompare();
    };

    const applyYear = (yp) => {
        setStartDate(yp.start);
        setEndDate(yp.end);
        setSelectedYear(yp.label);
        setSelectedMonth(null);
        setIsMax(false);
        clearCompare();
    };

    const fetchCompareData = useCallback(async (start, end) => {
        setCompareLoading(true);
        try {
            const result = await fetchDailySummaryRange(start, end, currentUser.user_id);
            setCompareData(result.data || []);
        } catch (err) {
            setCompareData([]);
        } finally {
            setCompareLoading(false);
        }
    }, [currentUser?.user_id]);

    const applyCompareMonth = useCallback(async (mp) => {
        if (compareMonth === mp.label) { clearCompare(); return; }
        setCompareMonth(mp.label);
        setCompareYear(null);
        await fetchCompareData(mp.start, mp.end);
    }, [compareMonth, fetchCompareData]);

    const applyCompareYear = useCallback(async (yp) => {
        if (compareYear === yp.label) { clearCompare(); return; }
        setCompareYear(yp.label);
        setCompareMonth(null);
        await fetchCompareData(yp.start, yp.end);
    }, [compareYear, fetchCompareData]);

    const handleMonthClick = (mp) => {
        if (!compareMode) { applyMonth(mp); return; }
        // In compare mode: if no primary selected yet, set primary
        if (!selectedMonth && !selectedYear) { applyMonth(mp); return; }
        if (mp.label === selectedMonth) return;
        applyCompareMonth(mp);
    };

    const handleYearClick = (yp) => {
        if (!compareMode) { applyYear(yp); return; }
        if (!selectedMonth && !selectedYear) { applyYear(yp); return; }
        if (yp.label === selectedYear) return;
        applyCompareYear(yp);
    };

    const toggleCompareMode = () => {
        setCompareMode((prev) => {
            if (prev) clearCompare();
            return !prev;
        });
    };

    const toggleMetric = (key) => {
        setVisibleMetrics((prev) =>
            prev.includes(key) ? prev.filter((m) => m !== key) : [...prev, key]
        );
    };

    const toggleEmployee = (name) => {
        setExcludedEmployees((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    };

    // Derive data based on selected location + employee exclusions + compute rev/hour
    const filteredData = normalizedData.map((d) => {
        const locData = selectedLocation === "all" ? d : (d[selectedLocation] || {});
        let base = {
            date: d.date,
            hours: locData.hours || 0,
            revenue: locData.revenue || 0,
            tips: locData.tips || 0,
            pets: locData.pets || 0,
            tickets: locData.tickets || 0,
            groomers: locData.groomers || 0,
            laborCost: locData.labor_cost || 0,
            employerTax: locData.employer_tax || 0,
            rent: locData.rent || 0,
        };

        // When employees are excluded, switch to additive: sum only included employees
        if (excludedEmployees.size > 0) {
            const empData = selectedLocation === "all" ? (d.by_employee || {}) : ((d[selectedLocation] || {}).by_employee || {});
            let sumHours = 0, sumRevenue = 0, sumTips = 0, sumPets = 0, sumTickets = 0, sumLabor = 0, sumTax = 0, groomCount = 0;
            for (const [name, emp] of Object.entries(empData)) {
                if (!excludedEmployees.has(name)) {
                    sumHours += emp.hours || 0;
                    sumRevenue += emp.revenue || 0;
                    sumTips += emp.tips || 0;
                    sumPets += emp.pets || 0;
                    sumTickets += emp.tickets || 0;
                    sumLabor += emp.labor_cost || 0;
                    sumTax += emp.employer_tax || 0;
                    if ((emp.hours || 0) > 0 || (emp.revenue || 0) > 0 || (emp.pets || 0) > 0) groomCount++;
                }
            }
            base.hours = Math.round(sumHours * 100) / 100;
            base.revenue = Math.round(sumRevenue * 100) / 100;
            base.tips = Math.round(sumTips * 100) / 100;
            base.pets = sumPets;
            base.tickets = sumTickets;
            base.groomers = groomCount;
            base.laborCost = Math.round(sumLabor * 100) / 100;
            base.employerTax = Math.round(sumTax * 100) / 100;
            // rent is not employee-specific, stays as-is
        }

        return {
            ...base,
            revPerHour: base.hours > 0 ? Math.round((base.revenue / base.hours) * 100) / 100 : 0,
        };
    });

    // Compute totals
    const daysWithData = filteredData.filter((d) => (d.hours || 0) > 0 || (d.revenue || 0) > 0).length;
    const totals = filteredData.reduce(
        (acc, d) => ({
            hours: acc.hours + (d.hours || 0),
            revenue: acc.revenue + (d.revenue || 0),
            tips: acc.tips + (d.tips || 0),
            pets: acc.pets + (d.pets || 0),
            groomers: acc.groomers + (d.groomers || 0),
            laborCost: acc.laborCost + (d.laborCost || 0),
            employerTax: acc.employerTax + (d.employerTax || 0),
            rent: acc.rent + (d.rent || 0),
        }),
        { hours: 0, revenue: 0, tips: 0, pets: 0, groomers: 0, laborCost: 0, employerTax: 0, rent: 0 }
    );
    const avgGroomers = daysWithData > 0 ? (totals.groomers / daysWithData).toFixed(1) : 0;

    const totalBonus = normalizedBonuses.reduce((sum, b) => sum + (b.total_bonus || 0), 0);

    // Helper: apply location + employee filters to a raw data array
    const applyFilters = (rawData) => rawData.map((d) => {
        const locData = selectedLocation === "all" ? d : (d[selectedLocation] || {});
        let base = {
            date: d.date,
            hours: locData.hours || 0,
            revenue: locData.revenue || 0,
            tips: locData.tips || 0,
            pets: locData.pets || 0,
            tickets: locData.tickets || 0,
            groomers: locData.groomers || 0,
            laborCost: locData.labor_cost || 0,
            employerTax: locData.employer_tax || 0,
            rent: locData.rent || 0,
        };
        if (excludedEmployees.size > 0) {
            const empData = selectedLocation === "all" ? (d.by_employee || {}) : ((d[selectedLocation] || {}).by_employee || {});
            let sH = 0, sR = 0, sT = 0, sP = 0, sTk = 0, sL = 0, sTx = 0, sG = 0;
            for (const [name, emp] of Object.entries(empData)) {
                if (!excludedEmployees.has(name)) {
                    sH += emp.hours || 0; sR += emp.revenue || 0; sT += emp.tips || 0;
                    sP += emp.pets || 0; sTk += emp.tickets || 0; sL += emp.labor_cost || 0; sTx += emp.employer_tax || 0;
                    if ((emp.hours || 0) > 0 || (emp.revenue || 0) > 0 || (emp.pets || 0) > 0) sG++;
                }
            }
            base = { ...base, hours: Math.round(sH * 100) / 100, revenue: Math.round(sR * 100) / 100, tips: Math.round(sT * 100) / 100, pets: sP, tickets: sTk, groomers: sG, laborCost: Math.round(sL * 100) / 100, employerTax: Math.round(sTx * 100) / 100 };
        }
        return { ...base, revPerHour: base.hours > 0 ? Math.round((base.revenue / base.hours) * 100) / 100 : 0 };
    });

    const filteredCompareData = applyFilters(normalizedCompareData);

    const compareTotals = filteredCompareData.reduce(
        (acc, d) => ({
            hours: acc.hours + (d.hours || 0), revenue: acc.revenue + (d.revenue || 0),
            tips: acc.tips + (d.tips || 0), pets: acc.pets + (d.pets || 0),
            groomers: acc.groomers + (d.groomers || 0),
            laborCost: acc.laborCost + (d.laborCost || 0), employerTax: acc.employerTax + (d.employerTax || 0),
            rent: acc.rent + (d.rent || 0),
        }),
        { hours: 0, revenue: 0, tips: 0, pets: 0, groomers: 0, laborCost: 0, employerTax: 0, rent: 0 }
    );
    const compareDaysWithData = filteredCompareData.filter((d) => (d.hours || 0) > 0 || (d.revenue || 0) > 0).length;


    // Apply cumulative transform to normal chart data
    const chartData = cumulative ? toCumulative(filteredData, CUMULATIVE_KEYS) : filteredData;

    // Build merged comparison chart data keyed by index (day-of-month for months, day-of-year for years)
    const compareLabel = compareMonth || compareYear;
    const primaryLabel = selectedMonth || selectedYear;
    const comparisonChartData = (() => {
        if (!compareMode || !compareLabel || filteredCompareData.length === 0) return null;
        const pData = cumulative ? toCumulative(filteredData, CUMULATIVE_KEYS) : filteredData;
        const cData = cumulative ? toCumulative(filteredCompareData, CUMULATIVE_KEYS) : filteredCompareData;
        const maxLen = Math.max(pData.length, cData.length);
        const merged = [];
        for (let i = 0; i < maxLen; i++) {
            const primary = pData[i] || {};
            const cmp = cData[i] || {};
            const row = { day: i + 1 };
            for (const key of Object.keys(METRIC_CONFIG)) {
                row[key] = primary[key] ?? null;
                row[`${key}_cmp`] = cmp[key] ?? null;
            }
            merged.push(row);
        }
        return merged;
    })();

    const isCompareActive = compareMode && compareLabel && comparisonChartData;

    return (
        <GenericModal
            name="businessTrendsModal"
            title="Business Trends"
            hideButton={true}
            modalMaxWidth="max-w-5xl"
        >
            <div className="p-5 space-y-4">
                {/* Date range controls */}
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 text-sm">
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm text-gray-700 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 outline-none"
                        />
                        <span className="text-gray-400">→</span>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm text-gray-700 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 outline-none"
                        />
                    </div>
                    <div className="flex gap-1.5">
                        {PRESETS.map((p) => (
                            <button
                                key={p.label}
                                onClick={() => applyPreset(p.days)}
                                className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                                    (p.days === null ? isMax : (new Date(endDate) - new Date(startDate)) / 86400000 + 1 === p.days)
                                        ? "bg-indigo-50 border-indigo-300 text-indigo-700 font-medium"
                                        : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                                }`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={fetchData}
                        disabled={loading}
                        className="ml-auto px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                    >
                        {loading ? "Loading..." : "Refresh"}
                    </button>
                </div>

                {/* Year presets + Month presets + Compare toggle + Cumulative */}
                <div className="flex flex-wrap items-center gap-1.5">
                    {YEAR_PRESETS.map((yp) => (
                        <button
                            key={yp.label}
                            onClick={() => handleYearClick(yp)}
                            className={`px-3 py-1 text-xs font-semibold rounded-md border transition-colors ${
                                compareYear === yp.label
                                    ? "bg-teal-50 border-teal-400 text-teal-700"
                                    : selectedYear === yp.label
                                        ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                                        : "border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50"
                            }`}
                        >
                            {yp.label}
                        </button>
                    ))}
                    <div className="w-px h-5 bg-gray-200 mx-0.5" />
                    {MONTH_PRESETS.map((mp) => (
                        <button
                            key={mp.label}
                            onClick={() => handleMonthClick(mp)}
                            className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                                compareMonth === mp.label
                                    ? "bg-teal-50 border-teal-400 text-teal-700 font-medium"
                                    : selectedMonth === mp.label
                                        ? "bg-indigo-50 border-indigo-300 text-indigo-700 font-medium"
                                        : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                            }`}
                        >
                            {mp.label}
                        </button>
                    ))}
                    <div className="w-px h-5 bg-gray-200 mx-1" />
                    <button
                        onClick={toggleCompareMode}
                        className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border transition-colors ${
                            compareMode
                                ? "bg-teal-50 border-teal-400 text-teal-700 font-medium"
                                : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                        }`}
                    >
                        <GitCompare size={12} />
                        Compare
                    </button>
                    <label className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md border border-gray-200 cursor-pointer hover:bg-gray-50 select-none">
                        <input
                            type="checkbox"
                            checked={cumulative}
                            onChange={(e) => setCumulative(e.target.checked)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3 h-3"
                        />
                        Cumulative
                    </label>
                    {compareMode && !compareLabel && (selectedMonth || selectedYear) && (
                        <span className="text-xs text-teal-600 italic">← pick a second month or year to compare</span>
                    )}
                    {compareMode && compareLabel && (
                        <span className="text-xs text-gray-500">
                            <span className="inline-block w-3 h-0.5 bg-indigo-500 align-middle mr-1" /> {primaryLabel}
                            <span className="mx-1.5">vs</span>
                            <span className="inline-block w-3 h-0.5 bg-teal-500 align-middle mr-1 border-b border-dashed" /> {compareLabel}
                        </span>
                    )}
                    {compareLoading && <Loader2 size={14} className="animate-spin text-teal-500" />}
                </div>

                {/* Location filter + Metric toggle pills */}
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex gap-1.5 mr-2">
                        {LOCATIONS.map((loc) => (
                            <button
                                key={loc.key}
                                onClick={() => setSelectedLocation(loc.key)}
                                className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                                    selectedLocation === loc.key
                                        ? "bg-gray-800 border-gray-800 text-white font-medium"
                                        : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                                }`}
                            >
                                {loc.label}
                            </button>
                        ))}
                    </div>
                    <div className="w-px h-5 bg-gray-200" />
                    {/* Employee filter dropdown */}
                    {displayEmployees.length > 0 && (
                        <div className="relative" ref={empDropdownRef}>
                            <button
                                onClick={() => setShowEmpDropdown((v) => !v)}
                                className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border transition-colors ${
                                    excludedEmployees.size > 0
                                        ? "bg-orange-50 border-orange-300 text-orange-700 font-medium"
                                        : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                                }`}
                            >
                                {excludedEmployees.size > 0 ? `${excludedEmployees.size} excluded` : "Employees"}
                                <ChevronDown size={12} />
                            </button>
                            {showEmpDropdown && (
                                <div className="absolute z-50 mt-1 left-0 bg-white border border-gray-200 rounded-lg shadow-xl p-2 w-56 max-h-64 overflow-y-auto">
                                    <div className="flex items-center justify-between mb-1.5 px-1">
                                        <span className="text-xs font-semibold text-gray-500">Exclude employees</span>
                                        <div className="flex gap-2">
                                            {excludedEmployees.size < displayEmployees.length && (
                                                <button onClick={() => setExcludedEmployees(new Set(displayEmployees))} className="text-xs text-orange-600 hover:underline">Exclude All</button>
                                            )}
                                            {excludedEmployees.size > 0 && (
                                                <button onClick={() => setExcludedEmployees(new Set())} className="text-xs text-indigo-600 hover:underline">Clear</button>
                                            )}
                                        </div>
                                    </div>
                                    {displayEmployees.map((name) => (
                                        <label key={name} className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-gray-50 cursor-pointer text-xs text-gray-700">
                                            <input
                                                type="checkbox"
                                                checked={excludedEmployees.has(name)}
                                                onChange={() => toggleEmployee(name)}
                                                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                                            />
                                            {name}
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <div className="w-px h-5 bg-gray-200" />
                    {Object.entries(METRIC_CONFIG).map(([key, cfg]) => (
                        <button
                            key={key}
                            onClick={() => toggleMetric(key)}
                            className={`flex items-center gap-1.5 px-3 py-1 text-xs rounded-full border transition-colors ${
                                visibleMetrics.includes(key)
                                    ? "border-current font-medium"
                                    : "border-gray-200 text-gray-400"
                            }`}
                            style={visibleMetrics.includes(key) ? { color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.color + "10" } : {}}
                        >
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: visibleMetrics.includes(key) ? cfg.color : "#d1d5db" }} />
                            {cfg.label}
                        </button>
                    ))}
                </div>

                {/* Chart */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="animate-spin text-indigo-500" size={32} />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center gap-2 py-20 text-red-500">
                        <AlertCircle size={18} />
                        <span className="text-sm">{error}</span>
                    </div>
                ) : normalizedData.length === 0 ? (
                    <div className="text-center py-20 text-gray-400 text-sm">No data for this date range</div>
                ) : isCompareActive ? (
                    <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={comparisonChartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#9ca3af" }} interval={comparisonChartData.length > 60 ? Math.floor(comparisonChartData.length / 12) : 0} />
                                {visibleMetrics.includes("hours") && (
                                    <YAxis yAxisId="hours" orientation="left" tick={{ fontSize: 11, fill: "#9ca3af" }} tickFormatter={(v) => `${v}h`} />
                                )}
                                {(visibleMetrics.includes("revenue") || visibleMetrics.includes("tips") || visibleMetrics.includes("laborCost") || visibleMetrics.includes("employerTax") || visibleMetrics.includes("rent")) && (
                                    <YAxis yAxisId="dollars" orientation="right" tick={{ fontSize: 11, fill: "#9ca3af" }} tickFormatter={(v) => `$${v}`} />
                                )}
                                {(visibleMetrics.includes("pets") || visibleMetrics.includes("groomers")) && !visibleMetrics.includes("hours") && (
                                    <YAxis yAxisId="count" orientation="left" tick={{ fontSize: 11, fill: "#9ca3af" }} />
                                )}
                                {visibleMetrics.includes("revPerHour") && !visibleMetrics.includes("revenue") && !visibleMetrics.includes("tips") && !visibleMetrics.includes("laborCost") && !visibleMetrics.includes("employerTax") && !visibleMetrics.includes("rent") && (
                                    <YAxis yAxisId="revPerHour" orientation="right" tick={{ fontSize: 11, fill: "#9ca3af" }} tickFormatter={(v) => `$${v}`} />
                                )}
                                <Tooltip content={<CustomTooltip visibleMetrics={visibleMetrics} compareMode={true} primaryLabel={primaryLabel} compareLabel={compareLabel} />} />
                                <Legend wrapperStyle={{ fontSize: "12px" }} formatter={(value) => {
                                    const isCmp = value.endsWith("_cmp");
                                    const baseKey = isCmp ? value.replace("_cmp", "") : value;
                                    const label = METRIC_CONFIG[baseKey]?.label || value;
                                    return `${label} (${isCmp ? compareLabel : primaryLabel})`;
                                }} />
                                {visibleMetrics.map((key) => {
                                    const cfg = METRIC_CONFIG[key];
                                    if (!cfg) return null;
                                    const yId = key === "hours" ? "hours"
                                        : (key === "pets" || key === "groomers") && !visibleMetrics.includes("hours") ? "count"
                                        : key === "revPerHour" && !visibleMetrics.includes("revenue") && !visibleMetrics.includes("tips") && !visibleMetrics.includes("laborCost") && !visibleMetrics.includes("employerTax") && !visibleMetrics.includes("rent") ? "revPerHour"
                                        : (key === "pets" || key === "groomers") ? "hours"
                                        : "dollars";
                                    return (
                                        <React.Fragment key={key}>
                                            <Line yAxisId={yId} type="monotone" dataKey={key} stroke={cfg.color} strokeWidth={2} dot={comparisonChartData.length <= 31} connectNulls />
                                            <Line yAxisId={yId} type="monotone" dataKey={`${key}_cmp`} stroke={cfg.color} strokeWidth={2} dot={comparisonChartData.length <= 31} strokeDasharray="6 3" opacity={0.5} connectNulls />
                                        </React.Fragment>
                                    );
                                })}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                ) : (
                    <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={formatDate}
                                    tick={{ fontSize: 11, fill: "#9ca3af" }}
                                    interval={chartData.length > 30 ? Math.floor(chartData.length / 10) : 0}
                                />
                                {visibleMetrics.includes("hours") && (
                                    <YAxis yAxisId="hours" orientation="left" tick={{ fontSize: 11, fill: "#9ca3af" }} tickFormatter={(v) => `${v}h`} />
                                )}
                                {(visibleMetrics.includes("revenue") || visibleMetrics.includes("tips") || visibleMetrics.includes("laborCost") || visibleMetrics.includes("employerTax") || visibleMetrics.includes("rent")) && (
                                    <YAxis yAxisId="dollars" orientation="right" tick={{ fontSize: 11, fill: "#9ca3af" }} tickFormatter={(v) => `$${v}`} />
                                )}
                                {(visibleMetrics.includes("pets") || visibleMetrics.includes("groomers")) && !visibleMetrics.includes("hours") && (
                                    <YAxis yAxisId="count" orientation="left" tick={{ fontSize: 11, fill: "#9ca3af" }} />
                                )}
                                {visibleMetrics.includes("revPerHour") && !visibleMetrics.includes("revenue") && !visibleMetrics.includes("tips") && !visibleMetrics.includes("laborCost") && !visibleMetrics.includes("employerTax") && !visibleMetrics.includes("rent") && (
                                    <YAxis yAxisId="revPerHour" orientation="right" tick={{ fontSize: 11, fill: "#9ca3af" }} tickFormatter={(v) => `$${v}`} />
                                )}
                                <Tooltip content={<CustomTooltip visibleMetrics={visibleMetrics} compareMode={false} />} />
                                <Legend
                                    formatter={(value) => METRIC_CONFIG[value]?.label || value}
                                    wrapperStyle={{ fontSize: "12px" }}
                                />
                                {visibleMetrics.includes("hours") && (
                                    <Line yAxisId="hours" type="monotone" dataKey="hours" stroke="#3b82f6" strokeWidth={2} dot={chartData.length <= 31} />
                                )}
                                {visibleMetrics.includes("groomers") && (
                                    <Line yAxisId={visibleMetrics.includes("hours") ? "hours" : "count"} type="monotone" dataKey="groomers" stroke="#0ea5e9" strokeWidth={2} dot={chartData.length <= 31} />
                                )}
                                {visibleMetrics.includes("revenue") && (
                                    <Line yAxisId="dollars" type="monotone" dataKey="revenue" stroke="#22c55e" strokeWidth={2} dot={chartData.length <= 31} />
                                )}
                                {visibleMetrics.includes("tips") && (
                                    <Line yAxisId="dollars" type="monotone" dataKey="tips" stroke="#a855f7" strokeWidth={2} dot={chartData.length <= 31} />
                                )}
                                {visibleMetrics.includes("laborCost") && (
                                    <Line yAxisId="dollars" type="monotone" dataKey="laborCost" stroke="#ef4444" strokeWidth={2} dot={chartData.length <= 31} />
                                )}
                                {visibleMetrics.includes("employerTax") && (
                                    <Line yAxisId="dollars" type="monotone" dataKey="employerTax" stroke="#dc2626" strokeWidth={2} dot={chartData.length <= 31} strokeDasharray="5 3" />
                                )}
                                {visibleMetrics.includes("rent") && (
                                    <Line yAxisId="dollars" type="monotone" dataKey="rent" stroke="#b45309" strokeWidth={2} dot={chartData.length <= 31} />
                                )}
                                {visibleMetrics.includes("pets") && (
                                    <Line yAxisId={visibleMetrics.includes("hours") ? "hours" : "count"} type="monotone" dataKey="pets" stroke="#f59e0b" strokeWidth={2} dot={chartData.length <= 31} />
                                )}
                                {visibleMetrics.includes("revPerHour") && (
                                    <Line yAxisId={(visibleMetrics.includes("revenue") || visibleMetrics.includes("tips") || visibleMetrics.includes("laborCost") || visibleMetrics.includes("employerTax") || visibleMetrics.includes("rent")) ? "dollars" : "revPerHour"} type="monotone" dataKey="revPerHour" stroke="#64748b" strokeWidth={2} dot={chartData.length <= 31} />
                                )}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {/* Summary totals */}
                {filteredData.length > 0 && !loading && !isCompareActive && (
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 pt-2">
                        <div className="rounded-xl border p-3 bg-blue-50 border-blue-200">
                            <div className="text-xs font-medium text-blue-600 mb-0.5">Total Hours</div>
                            <div className="text-lg font-bold text-blue-700">{totals.hours.toFixed(1)}h</div>
                        </div>
                        <div className="rounded-xl border p-3 bg-green-50 border-green-200">
                            <div className="text-xs font-medium text-green-600 mb-0.5">Total Revenue</div>
                            <div className="text-lg font-bold text-green-700">${totals.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                        <div className="rounded-xl border p-3 bg-purple-50 border-purple-200">
                            <div className="text-xs font-medium text-purple-600 mb-0.5">Total Tips</div>
                            <div className="text-lg font-bold text-purple-700">${totals.tips.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                        <div className="rounded-xl border p-3 bg-amber-50 border-amber-200">
                            <div className="text-xs font-medium text-amber-600 mb-0.5">Total Pets</div>
                            <div className="text-lg font-bold text-amber-700">{totals.pets}</div>
                        </div>
                        <div className="rounded-xl border p-3 bg-sky-50 border-sky-200">
                            <div className="text-xs font-medium text-sky-600 mb-0.5">Avg Groomers/Day</div>
                            <div className="text-lg font-bold text-sky-700">{avgGroomers}</div>
                        </div>
                        <div className="rounded-xl border p-3 bg-red-50 border-red-200">
                            <div className="text-xs font-medium text-red-600 mb-0.5">Base + Merit Cost <span className="text-red-400 font-normal">(excl. bonus)</span></div>
                            <div className="text-lg font-bold text-red-700">${totals.laborCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                        <div className="rounded-xl border p-3 bg-red-50 border-red-200">
                            <div className="text-xs font-medium text-red-600 mb-0.5">Employer Tax <span className="text-red-400 font-normal">(7.65%)</span></div>
                            <div className="text-lg font-bold text-red-700">${totals.employerTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                        <div className="rounded-xl border p-3 bg-amber-50 border-amber-300">
                            <div className="text-xs font-medium text-amber-700 mb-0.5">Rent</div>
                            <div className="text-lg font-bold text-amber-800">${totals.rent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                        {totalBonus > 0 && (
                            <div className="rounded-xl border p-3 bg-pink-50 border-pink-200">
                                <div className="text-xs font-medium text-pink-600 mb-0.5">Total Bonuses</div>
                                <div className="text-lg font-bold text-pink-700">${totalBonus.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                            </div>
                        )}
                    </div>
                )}

                {/* Comparison summary totals */}
                {isCompareActive && !loading && (
                    <div className="pt-2">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {[
                                { key: "hours",   label: "Hours",      bg: "bg-blue-50",   border: "border-blue-200",  title: "text-blue-600",  val: "text-blue-700",   fmtA: (v) => `${v.toFixed(1)}h`, fmtB: (v) => `${v.toFixed(1)}h` },
                                { key: "revenue", label: "Revenue",    bg: "bg-green-50",  border: "border-green-200", title: "text-green-600", val: "text-green-700",  fmtA: (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, fmtB: (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
                                { key: "tips",    label: "Tips",       bg: "bg-purple-50", border: "border-purple-200",title: "text-purple-600",val: "text-purple-700", fmtA: (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, fmtB: (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
                                { key: "pets",    label: "Pets",       bg: "bg-amber-50",  border: "border-amber-200", title: "text-amber-600", val: "text-amber-700",  fmtA: (v) => v, fmtB: (v) => v },
                                { key: "groomers", label: "Avg Groomers/Day", bg: "bg-sky-50", border: "border-sky-200", title: "text-sky-600", val: "text-sky-700", isAvg: true, fmtA: (v) => v, fmtB: (v) => v },
                                { key: "laborCost", label: "Base + Merit", bg: "bg-red-50", border: "border-red-200",  title: "text-red-600",   val: "text-red-700",    fmtA: (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, fmtB: (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
                                { key: "rent",    label: "Rent",       bg: "bg-amber-50",  border: "border-amber-300", title: "text-amber-700", val: "text-amber-800",  fmtA: (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, fmtB: (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
                            ].map(({ key, label, bg, border, title, val, fmtA, isAvg }) => {
                                const rawA = totals[key] || 0;
                                const rawB = compareTotals[key] || 0;
                                const a = isAvg ? (daysWithData > 0 ? +(rawA / daysWithData).toFixed(1) : 0) : rawA;
                                const b = isAvg ? (compareDaysWithData > 0 ? +(rawB / compareDaysWithData).toFixed(1) : 0) : rawB;
                                const diff = a - b;
                                const pct = b !== 0 ? ((diff / Math.abs(b)) * 100).toFixed(1) : null;
                                return (
                                    <div key={key} className={`rounded-xl border p-3 ${bg} ${border}`}>
                                        <div className={`text-xs font-medium ${title} mb-1`}>{label}</div>
                                        <div className="flex items-baseline gap-2">
                                            <div className={`text-lg font-bold ${val}`}>{fmtA(a)}</div>
                                            <div className="text-xs text-gray-400">vs {fmtA(b)}</div>
                                        </div>
                                        {pct !== null && (
                                            <div className={`text-xs font-medium mt-0.5 ${diff > 0 ? "text-green-600" : diff < 0 ? "text-red-500" : "text-gray-400"}`}>
                                                {diff > 0 ? "↑" : diff < 0 ? "↓" : "="} {Math.abs(diff) < 0.01 ? "0%" : `${Math.abs(Number(pct))}%`}
                                                <span className="text-gray-400 font-normal ml-1">({primaryLabel} vs {compareLabel})</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Bonus breakdown per pay period */}
                {normalizedBonuses.some((b) => b.total_bonus > 0) && !loading && (
                    <div className="pt-1">
                        <div className="flex items-center gap-1.5 mb-2">
                            <Gift size={14} className="text-pink-500" />
                            <span className="text-xs font-semibold text-gray-600">Bonus by Pay Period</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {normalizedBonuses.filter((b) => b.total_bonus > 0).map((b) => (
                                <div key={b.period_start} className="rounded-lg border border-pink-100 bg-pink-50/50 p-2.5 text-xs">
                                    <div className="flex justify-between items-center mb-1.5">
                                        <span className="font-medium text-gray-600">
                                            {new Date(b.period_start + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                            {" – "}
                                            {new Date(b.period_end + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                        </span>
                                        <span className="font-bold text-pink-700">${b.total_bonus.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                    </div>
                                    <div className="space-y-0.5">
                                        {Object.entries(b.by_employee || {}).sort((a, b) => b[1] - a[1]).map(([name, amt]) => (
                                            <div key={name} className="flex justify-between text-gray-500">
                                                <span>{name}</span>
                                                <span className="text-pink-600 font-medium">${amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </GenericModal>
    );
};

export default BusinessTrendsModal;
